"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageSquareWarning } from "@/components/icons";
import { ReportIssueForm, type ReportIssueContext } from "./report-issue-form";

interface ReportIssueDialogProps {
  context?: ReportIssueContext;
  triggerLabel?: string;
  triggerVariant?: "destructive-ghost" | "link";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
  triggerless?: boolean;
}

function ReportIssueTrigger({
  triggerLabel,
  triggerVariant,
  children,
}: {
  triggerLabel: string;
  triggerVariant: NonNullable<ReportIssueDialogProps["triggerVariant"]>;
  children?: React.ReactNode;
}) {
  if (children) {
    return <DialogTrigger asChild>{children}</DialogTrigger>;
  }
  return (
    <DialogTrigger asChild>
      <Button
        type="button"
        size="auto"
        variant={triggerVariant === "link" ? "link" : "ghost"}
        className={
          triggerVariant === "destructive-ghost"
            ? "gap-1 px-2 py-1 text-xs text-destructive-strong hover:bg-destructive-bg hover:text-destructive-strong"
            : "gap-1 px-0 text-xs"
        }
      >
        <MessageSquareWarning className="h-3.5 w-3.5" />
        {triggerLabel}
      </Button>
    </DialogTrigger>
  );
}

export function ReportIssueDialog({
  context,
  triggerLabel = "Describe this issue",
  triggerVariant = "destructive-ghost",
  open,
  onOpenChange,
  children,
  triggerless = false,
}: ReportIssueDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setIsOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {triggerless ? null : (
        <ReportIssueTrigger triggerLabel={triggerLabel} triggerVariant={triggerVariant}>
          {children}
        </ReportIssueTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report this issue</DialogTitle>
        </DialogHeader>
        <ReportIssueForm
          context={context}
          variant="compact"
          heading=""
          onSuccess={() => {
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
